import { useCallback, useState } from 'react';

import CreateAutoscaleRulesModal from './CreateAutoscaleRulesModal/CreateAutoscaleRulesModal';
import { useAutoscaleRulesBuilder } from './CreateAutoscaleRulesModal/useAutoscaleRulesBuilder';
import AutoscaleRulesFiltersSidePanel from './AutoscaleRulesFiltersSidePanel';
import { useAutoscaleRulesQuery } from './AutoscaleRulesTable/hooks/useAutoscaleRulesQuery';
import AutoscaleRulesTable from './AutoscaleRulesTable/AutoscaleRulesTable';
import AutoscaleRulesEmptyState from './AutoscaleRulesEmptyState';
import AutoscaleRulesHeader from './AutoscaleRulesHeader';
import { useAutoscaleRulesFilters } from './hooks/useAutoscaleRulesFilters';

export default function AutoscaleRulesPage() {
  const [isCreateNewRuleModalOpen, setCreateNewRuleModalOpen] = useState(false);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<{
    ruleId: string;
    rawRule: Record<string, unknown>;
  } | null>(null);

  const builder = useAutoscaleRulesBuilder();

  const {
    filters,
    appliedRuleIDFilters,
    appliedAutoscaleState,
    apiFilters,
    handleResetFilters,
    handleApplyFilters,
  } = useAutoscaleRulesFilters();

  const { data, isLoading, isError } = useAutoscaleRulesQuery({
    filters,
    apiFilters,
    dryRun: true,
  });

  const toggleSidePanel = useCallback(() => setSidePanelOpen((o) => !o), []);

  const handleEditRule = useCallback(
    (ruleId: string, rawRule: Record<string, unknown>) => {
      setEditingRule({ ruleId, rawRule });
      builder.loadFromJsonLogic(rawRule);
      setCreateNewRuleModalOpen(true);
    },
    [builder]
  );

  const handleCloseModal = useCallback(() => {
    setCreateNewRuleModalOpen(false);
    // Delay resetting to allow modal close animation to complete
    setTimeout(() => {
      setEditingRule(null);
      builder.resetBuilder();
    }, 300);
  }, [builder]);

  // console.log({ builder });
  // console.log(sidePanelOpen, 'sidePanelOpen');
  // console.log('Rules data:', data);

  const hasNoData = !isLoading && (!data?.rules || data.rules.length === 0);
  const shouldShowEmptyState =
    hasNoData &&
    !(
      (filters.active && filters.active.length > 0) ||
      (filters.ruleIds && filters.ruleIds.length > 0)
    ) &&
    !isError;

  return (
    <div className="flex flex-col h-full">
      {!shouldShowEmptyState && (
        <AutoscaleRulesHeader
          rulesCount={isLoading ? undefined : data?.count ?? 0}
          activeFilterCount={(filters.active?.length || 0) + (filters.ruleIds?.length || 0)}
          onOpenFilters={toggleSidePanel}
          onClearFilters={handleResetFilters}
          onCreateRule={() => setCreateNewRuleModalOpen(true)}
        />
      )}

      <AutoscaleRulesFiltersSidePanel
        open={sidePanelOpen}
        onOpenChange={(open: boolean) => {
          if (sidePanelOpen) {
            setSidePanelOpen(open);
          } else {
            // Fallback: toggle only when change differs to avoid accidental close/open loops
            if (open !== sidePanelOpen) {
              toggleSidePanel();
            }
          }
        }}
        onResetFilters={handleResetFilters}
        onApplyFilters={handleApplyFilters}
        appliedRuleIDFilters={appliedRuleIDFilters}
        appliedAutoscaleState={appliedAutoscaleState}
      />

      <div className="flex-1 min-h-0">
        {shouldShowEmptyState ? (
          <div className="py-10 h-full">
            <AutoscaleRulesEmptyState onCreateRule={() => setCreateNewRuleModalOpen(true)} />
          </div>
        ) : (
          <AutoscaleRulesTable
            loading={isLoading}
            rules={data?.rules || []}
            onEditRule={handleEditRule}
          />
        )}
      </div>

      <CreateAutoscaleRulesModal
        isOpen={isCreateNewRuleModalOpen}
        onClose={handleCloseModal}
        builder={builder}
        editMode={!!editingRule}
        ruleId={editingRule?.ruleId}
      />
    </div>
  );
}
